import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

function BootScreen({ lines, typingSpeed = 120, fadeDelayMs = 3000, onDone }) {
  const fullText = useMemo(() => (lines || []).join('\n'), [lines]);
  const [index, setIndex] = useState(0);
  const [readyToFade, setReadyToFade] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [fontReady, setFontReady] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth <= 768 || window.innerHeight <= 560,
  );
  const finishedRef = useRef(false);

  const finishNow = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onDone();
  }, [onDone]);

  useEffect(() => {
    let mounted = true;
    const fallback = window.setTimeout(() => {
      if (mounted) setFontReady(true);
    }, 1800);

    async function warmupFonts() {
      try {
        if (document.fonts && document.fonts.load) {
          await Promise.all([
            document.fonts.load("700 24px 'Carbon Black'"),
            document.fonts.load("400 24px 'STHeiti'"),
          ]);
        }
      } catch (error) {
        // ignore and use fallback timer
      } finally {
        if (mounted) setFontReady(true);
      }
    }

    warmupFonts();
    return () => {
      mounted = false;
      window.clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    function onResize() {
      setIsSmallScreen(window.innerWidth <= 768 || window.innerHeight <= 560);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!fontReady) return;
    if (!fullText) {
      setReadyToFade(true);
      return;
    }
    if (index >= fullText.length) {
      setReadyToFade(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setIndex(current => current + 1);
    }, typingSpeed);
    return () => window.clearTimeout(timer);
  }, [fontReady, fullText, index, typingSpeed]);

  useEffect(() => {
    if (!readyToFade) return;
    const timer = window.setTimeout(() => {
      setFadingOut(true);
      window.setTimeout(() => finishNow(), 650);
    }, fadeDelayMs);
    return () => window.clearTimeout(timer);
  }, [readyToFade, fadeDelayMs, finishNow]);

  return (
    <Container className={fadingOut ? 'fade-out' : ''}>
      <button className="skip-button" onClick={finishNow} type="button">
        跳过动画
      </button>
      {isSmallScreen && (
        <div className="small-screen-tip">提示：在电脑屏幕上浏览本网站体验更佳。</div>
      )}
      <pre>
        {fontReady ? fullText.slice(0, index) : ''}
        {fontReady && index >= fullText.length && <span className="cursor" />}
      </pre>
    </Container>
  );
}

const Container = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  color: #fff;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 650ms ease;
  &.fade-out {
    opacity: 0;
    pointer-events: none;
  }
  pre {
    width: min(760px, 92vw);
    margin: 0;
    white-space: pre-wrap;
    line-height: 1.65;
    font-size: clamp(20px, 2.3vw, 32px);
    font-family: 'Carbon Black', 'STHeiti', 'Microsoft YaHei', sans-serif;
    letter-spacing: 0.02em;
  }
  .cursor {
    display: inline-block;
    width: 0.62em;
    height: 1em;
    background: #fff;
    transform: translateY(0.1em);
    margin-left: 0.12em;
    animation: ${blink} 1s steps(1, end) infinite;
  }
  .skip-button {
    position: absolute;
    right: 14px;
    bottom: 12px;
    background: transparent;
    border: 0;
    color: rgba(210, 210, 210, 0.65);
    font-size: 11px;
    line-height: 1;
    cursor: pointer;
    padding: 2px 3px;
    &:hover {
      color: rgba(240, 240, 240, 0.92);
      text-decoration: underline;
    }
  }
  .small-screen-tip {
    position: absolute;
    left: 14px;
    bottom: 12px;
    font-size: 11px;
    color: #f2d24d;
    letter-spacing: 0.02em;
    opacity: 0.95;
  }
`;

export default BootScreen;
